import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

import DocumentLibrary from './DocumentLibrary';
import ParticipantsManager from './ParticipantsManager';
import SecureMessaging from './SecureMessaging';
import ActivityLog from './ActivityLog';
import PatientJourneyTab from './PatientJourneyTab';
import AutomationHub from './AutomationHub';

const { FiArrowLeft, FiFileText, FiUsers, FiMessageSquare, FiActivity, FiClipboard, FiZap } = FiIcons;

const mockDealrooms = {
  1: { id: 1, title: 'Implant Case - John Doe', patient: 'John Doe', caseType: 'Dental Implant', status: 'Active', stage: 'Treatment Planning', created: '2024-07-20', lastUpdate: '2024-07-21' },
  2: { id: 2, title: 'Full Arch Restoration - Jane Smith', patient: 'Jane Smith', caseType: 'Full Arch Implant Therapy', status: 'Completed', stage: 'Post-Op', created: '2024-07-15', lastUpdate: '2024-07-18' },
  3: { id: 3, title: 'Wisdom Teeth Extraction - Mike Johnson', patient: 'Mike Johnson', caseType: 'Wisdom Teeth', status: 'Archived', stage: 'Closed', created: '2024-06-01', lastUpdate: '2024-06-10' },
  4: { id: 4, title: 'Jaw Surgery - Emily White', patient: 'Emily White', caseType: 'Jaw Surgery', status: 'Active', stage: 'Pre-Op Assessment', created: '2024-07-18', lastUpdate: '2024-07-21' },
  5: { id: 5, title: 'Custom Case - New Patient', patient: 'New Patient', caseType: 'Other', status: 'Active', stage: 'Initial Consultation', created: '2024-07-22', lastUpdate: '2024-07-22' },
};

const DealroomDetail = () => {
  const { dealId } = useParams();
  const [dealroom, setDealroom] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const foundDealroom = mockDealrooms[dealId] || mockDealrooms[5];
      setDealroom(foundDealroom);
      setLoading(false);
    }, 500);
  }, [dealId]);

  const tabs = [
    { id: 'documents', label: 'Documents', icon: FiFileText },
    { id: 'participants', label: 'Participants', icon: FiUsers },
    { id: 'messages', label: 'Messages', icon: FiMessageSquare },
    { id: 'journey', label: 'Patient Journey', icon: FiClipboard },
    { id: 'automations', label: 'Automations', icon: FiZap },
    { id: 'activity', label: 'Activity Log', icon: FiActivity },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-brand-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Dealroom...</p>
        </div>
      </div>
    );
  }

  if (!dealroom) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-red-600">Dealroom Not Found</h2>
        <p className="text-gray-600 mt-2">The dealroom you are looking for does not exist.</p>
        <Link to="/dealrooms" className="mt-4 inline-block bg-brand-primary text-white font-semibold py-2 px-4 rounded-lg">
          Back to Dealrooms
        </Link>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'documents':
        return <DocumentLibrary dealId={dealId} />;
      case 'participants':
        return <ParticipantsManager dealId={dealId} />;
      case 'messages':
        return <SecureMessaging dealId={dealId} />;
      case 'journey':
        return <PatientJourneyTab patientName={dealroom.patient} />;
      case 'automations':
        return <AutomationHub dealId={dealId} />;
      case 'activity':
        return <ActivityLog dealId={dealId} />;
      default:
        return <DocumentLibrary dealId={dealId} />;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="p-4 md:p-8">
        <div className="mb-6">
          <Link to="/dealrooms" className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand-primary font-semibold transition-colors">
            <SafeIcon icon={FiArrowLeft} />
            Back to all Dealrooms
          </Link>
        </div>

        <header className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <div className="flex flex-col md:flex-row justify-between md:items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{dealroom.title}</h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span>Case ID: DR00{dealroom.id}</span>
                <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                <span>Patient: {dealroom.patient}</span>
                <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                <span className={`font-semibold px-2 py-0.5 rounded-full ${
                  dealroom.status === 'Active' ? 'bg-green-100 text-green-800' :
                  dealroom.status === 'Completed' ? 'bg-indigo-100 text-indigo-800' :
                  'bg-gray-100 text-gray-800'
                }`}>{dealroom.status}</span>
              </div>
            </div>
            <div className="mt-4 md:mt-0 text-sm md:text-right">
              <p className="text-gray-500">Current Stage</p>
              <p className="font-bold text-lg text-brand-primary">{dealroom.stage}</p>
            </div>
          </div>
        </header>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex space-x-2 md:space-x-4 px-6" aria-label="Tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center gap-2 py-4 px-1 md:px-4 border-b-2 font-semibold text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-brand-primary text-brand-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealroomDetail;